import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  Buildings,
  ClipboardText,
  SignOut,
  Users,
  Gauge,
  CaretLeft,
  CaretRight,
  Database,
  ChartLineUp,
} from "@phosphor-icons/react";

const STORAGE_KEY = "audit_sidebar_collapsed";

export function useSidebarState() {
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(STORAGE_KEY) === "1"
  );
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, collapsed ? "1" : "0");
  }, [collapsed]);
  return [collapsed, setCollapsed];
}

export default function Sidebar({ collapsed, setCollapsed }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const role = user?.role;

  const auditTeamItems = [
    { to: "/audit/dashboard", label: "Overview", icon: <Gauge size={18} /> },
    { to: "/audit/partner", label: "Partner View", icon: <ChartLineUp size={18} /> },
    { to: "/audit/companies", label: "Clients", icon: <Buildings size={18} /> },
    { to: "/audit/users", label: "Client Users", icon: <Users size={18} /> },
    { to: "/audit/data-tools", label: "Data Tools", icon: <Database size={18} /> },
  ];
  const auditPartnerItems = [
    { to: "/audit/partner", label: "Partner Overview", icon: <ChartLineUp size={18} /> },
    { to: "/audit/companies", label: "Clients", icon: <Buildings size={18} /> },
  ];
  const clientItems = [
    { to: "/client/dashboard", label: "My Dashboard", icon: <Gauge size={18} /> },
    { to: "/client/observations", label: "Observations", icon: <ClipboardText size={18} /> },
    { to: "/client/profile", label: "Company Profile", icon: <Buildings size={18} /> },
  ];
  const founderItems = [
    { to: "/client/founder", label: "Founder's View", icon: <ChartLineUp size={18} /> },
    { to: "/client/dashboard", label: "Operational", icon: <Gauge size={18} /> },
    { to: "/client/observations", label: "Observations", icon: <ClipboardText size={18} /> },
    { to: "/client/profile", label: "Company Profile", icon: <Buildings size={18} /> },
  ];
  const items =
    role === "audit_team" ? auditTeamItems :
    role === "audit_partner" ? auditPartnerItems :
    role === "client_founder" ? founderItems :
    clientItems;

  return (
    <aside
      className={`hidden md:flex md:flex-col bg-white border-r border-slate-200 text-slate-800 h-screen fixed left-0 top-0 z-30 transition-[width] duration-150 ease-out shadow-sm ${
        collapsed ? "w-[68px]" : "w-[240px]"
      }`}
      data-testid="sidebar"
    >
      {/* Brand — both versions kept mounted to prevent image reload on toggle */}
      <div className="px-3 py-4 border-b border-slate-200 flex items-center gap-2 min-h-[60px]">
        <div className={`${collapsed ? "block" : "hidden"} w-9 h-9 rounded-md bg-[#0B2A5B] flex items-center justify-center shrink-0`}>
          <span className="text-white font-bold text-sm">D<span className="text-[#A8B86C]">.</span></span>
        </div>
        <img
          src="/brand/delhigence-logo-sm.png"
          alt="DELHiGENCE.AI"
          className={`${collapsed ? "hidden" : "block"} h-6 w-auto`}
          data-testid="brand-logo"
        />
        <button
          onClick={() => setCollapsed((v) => !v)}
          className="ml-auto p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-900"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          data-testid="sidebar-toggle"
        >
          {collapsed ? <CaretRight size={14} weight="bold" /> : <CaretLeft size={14} weight="bold" />}
        </button>
      </div>

      {/* Role pill */}
      {!collapsed && (
        <div className="px-3 pt-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 px-1">
            {role === "audit_team" && "Audit Console"}
            {role === "audit_partner" && "Partner Console"}
            {role === "client_founder" && "Founder Console"}
            {role === "client" && "Client Portal"}
          </div>
        </div>
      )}

      {/* Nav */}
      <nav className="px-2 py-3 flex-1 space-y-0.5 overflow-y-auto">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                isActive
                  ? "bg-[#0B2A5B] text-white"
                  : "text-slate-700 hover:bg-slate-100"
              } ${collapsed ? "justify-center px-2" : ""}`
            }
            title={collapsed ? it.label : ""}
            data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {it.icon}
            {!collapsed && <span>{it.label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* User */}
      <div className="px-2 py-3 border-t border-slate-200">
        <div className={`flex items-center gap-3 px-2 py-2 ${collapsed ? "justify-center" : ""}`}>
          <div className="w-9 h-9 rounded-full bg-[#0B2A5B] flex items-center justify-center text-sm font-semibold text-white shrink-0">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          {!collapsed && (
            <>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{user?.name}</div>
                <div className="text-[11px] text-slate-500 truncate">{user?.email}</div>
              </div>
              <button
                onClick={() => {
                  logout();
                  navigate("/login");
                }}
                className="p-2 rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-900"
                data-testid="logout-btn"
                title="Sign out"
              >
                <SignOut size={18} />
              </button>
            </>
          )}
        </div>
        {collapsed && (
          <button
            onClick={() => {
              logout();
              navigate("/login");
            }}
            className="w-full flex items-center justify-center mt-1 p-2 rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-900"
            data-testid="logout-btn-collapsed"
            title="Sign out"
          >
            <SignOut size={18} />
          </button>
        )}
      </div>
    </aside>
  );
}
